/** The six evidence links of one figure, each in one of four honest states - P15 item 2.
 *
 * **Nothing here is a second opinion.** The server's assessment (`inventory/evidence_status.py`) is
 * the one judgement of whether a figure is traceable and arrives on the receipt as `evidence_met`,
 * `evidence_not_applicable`, `evidence_broken_at` and `evidence_why_not`. This module only lays those
 * four out, link by link, and it never adds them up: there is no percentage and no count, because the
 * server stops at the first break and a count would claim to have looked at what it did not.
 *
 * Four states, none collapsed into another:
 *
 * - `met`: the server established the link.
 * - `unmet`: the link the server named as the first break, with its sentence. Only that one: the
 *   server says nothing about the links after it, so they are not `unmet`.
 * - `unchecked`: the server did not establish it and did not name it either (it stopped earlier, or
 *   the check was skipped). Drawing this as met would be a claim and as unmet would be another.
 * - `na`: the link cannot apply to this figure (a hand-entered figure has no document to store), with
 *   the reason, because *not applicable* is not a step that failed.
 *
 * A receipt that is complete and lists no links (a response that predates the lists) has every link
 * met, which is `receiptLinks.linkStanding`'s own reading of the same fields.
 */

import { LINK_STEPS } from "../components/receiptLinks";
import type { LinkStep } from "../components/receiptLinks";
import type { Receipt } from "../api/queries";

export type MeterState = "met" | "unmet" | "unchecked" | "na";

export interface MeterLink {
  step: LinkStep;
  label: string;
  state: MeterState;
  /** Why it is unmet, or why it does not apply. Empty for a met or unchecked link. */
  reason: string;
  /** The receipt chain node that explains it, so a click lands on the part that says why. */
  part: ReceiptPart;
}

/** The nodes of the receipt's chain (`receiptLinks.NodeKey`) that a link is explained by. */
export type ReceiptPart = "source" | "verifier" | "factor" | "ledger";

const LABEL: Readonly<Record<LinkStep, string>> = {
  referenced: "Referenced",
  stored: "Stored",
  retrievable: "Retrievable",
  verified: "Verified",
  priced: "Priced",
  ledgered: "In the ledger",
};

export const PART_OF: Readonly<Record<LinkStep, ReceiptPart>> = {
  referenced: "source",
  stored: "source",
  retrievable: "source",
  verified: "verifier",
  priced: "factor",
  ledgered: "ledger",
};

function notApplicableBecause(receipt: Receipt, step: LinkStep): string {
  if (receipt.reference_kind === "stated_basis" && (step === "stored" || step === "retrievable")) {
    return "A person stated this figure's basis, so there is no document to store or retrieve.";
  }
  return "This link does not apply to this figure.";
}

export function meterOf(receipt: Receipt): MeterLink[] {
  const met: readonly string[] = receipt.evidence_met ?? [];
  const na: readonly string[] = receipt.evidence_not_applicable ?? [];
  const brokenAt = receipt.evidence_broken_at ?? null;
  const listed = met.length > 0 || na.length > 0 || brokenAt !== null;
  return LINK_STEPS.map((step): MeterLink => {
    const base = { step, label: LABEL[step], part: PART_OF[step] };
    if (met.includes(step)) return { ...base, state: "met", reason: "" };
    if (na.includes(step)) {
      return { ...base, state: "na", reason: notApplicableBecause(receipt, step) };
    }
    if (brokenAt === step) {
      return {
        ...base,
        state: "unmet",
        reason: receipt.evidence_why_not || "EmitIQ did not establish this link.",
      };
    }
    // A complete receipt that lists nothing predates the lists; the same fallback as `linkStanding`.
    if (receipt.is_complete && !listed) return { ...base, state: "met", reason: "" };
    return { ...base, state: "unchecked", reason: "" };
  });
}

/** The words a state is spoken as. Shape and word, never colour alone (design rule 4). */
export const STATE_WORD: Readonly<Record<MeterState, string>> = {
  met: "met",
  unmet: "not met",
  unchecked: "not checked",
  na: "not applicable",
};
